import React, { useRef, useEffect, useState } from 'react';

const AnimatedOnScroll = ({ children, animationClass, delay = 0 }) => {
  const domRef = useRef();
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setVisible(true);
        observer.unobserve(domRef.current);
      }
    }, { threshold: 0.5 });

    if (domRef.current) observer.observe(domRef.current);

    return () => {
      if (domRef.current) observer.unobserve(domRef.current);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={domRef}
      className={`${isVisible ? animationClass : 'opacity-0 transform translate-y-4'} transition-all duration-700 ease-out`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const RefundPolicyPage = () => {
  const [policyData, setPolicyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReturnPolicy = async () => {
      try {
        const response = await fetch('https://aman-life-style-bd-main-server-974485829900.asia-south1.run.app/api/return-policy');
        const data = await response.json();
        if (data.success) {
          setPolicyData(data.policy);
        } else {
          setError(data.error || 'Failed to load return policy');
        }
      } catch (err) {
        setError('Failed to load return policy');
        console.error('Error fetching return policy:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReturnPolicy();
  }, []);

  const appleFont = {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif"
  };

  if (loading) {
    return (
      <div style={appleFont} className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500 tracking-wide">Loading return policy...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={appleFont} className="bg-white min-h-screen flex items-center justify-center">
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div style={appleFont} className="bg-white min-h-screen">
      <div className="max-w-3xl mx-auto px-6 py-16 my-8 border border-gray-200 rounded-xl shadow-sm">

        {/* Header */}
        <AnimatedOnScroll animationClass="animate-fade-in-down">
          <div className="mb-14">
            <p className="text-xs font-medium tracking-widest text-gray-400 uppercase mb-3">Policy</p>
            <h1 className="text-4xl font-semibold text-gray-900 tracking-tight leading-tight">
              {policyData?.title || 'Return Policy'}
            </h1>
            <p className="mt-2 text-sm text-gray-400">
              {policyData?.lastUpdated || 'Last Updated: January 2026'}
            </p>
          </div>
        </AnimatedOnScroll>

        {/* Introduction */}
        <AnimatedOnScroll animationClass="animate-fade-in-up">
          <div className="mb-12 pb-12 border-b border-gray-100">
            <p className="text-base text-gray-600 leading-relaxed">
              {policyData?.introduction || 'At "Elevate Mart", we value your shopping experience. If you are not satisfied with your purchase, you can return or exchange the product following our return policy.'}
            </p>
          </div>
        </AnimatedOnScroll>

        {/* Return Timeline */}
        <AnimatedOnScroll animationClass="animate-fade-in-left" delay={100}>
          <div className="mb-12 pb-12 border-b border-gray-100">
            <h2 className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-4">Return Timeline</h2>
            <p
              className="text-base text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: policyData?.returnTimeline || 'You can apply for a return or exchange within <strong>7 (seven) days</strong> from the delivery date. No requests will be accepted after this period.'
              }}
            />
          </div>
        </AnimatedOnScroll>

        {/* Return Conditions */}
        <AnimatedOnScroll animationClass="animate-fade-in-left" delay={200}>
          <div className="mb-12 pb-12 border-b border-gray-100">
            <h2 className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-6">Return Conditions</h2>
            <div className="space-y-1">
              {policyData?.conditions && policyData.conditions.length > 0 ? (
                policyData.conditions.map((condition, index) => (
                  <AnimatedOnScroll key={index} animationClass="animate-fade-in-right" delay={300 + index * 50}>
                    <div className="flex items-start gap-3 py-3 border-b border-gray-50">
                      <span className={`mt-0.5 text-sm flex-shrink-0 ${condition.type === 'allowed' ? 'text-gray-900' : 'text-red-400'}`}>
                        {condition.type === 'allowed' ? '✓' : '✕'}
                      </span>
                      <p className={`text-sm leading-relaxed ${condition.type === 'allowed' ? 'text-gray-700' : 'text-gray-500'}`}>
                        {condition.text}
                      </p>
                    </div>
                  </AnimatedOnScroll>
                ))
              ) : (
                [
                  { type: 'allowed', text: 'The product must be unused and in its original condition.', delay: 300 },
                  { type: 'allowed', text: 'You must record a video while unboxing the product. If there are any issues, send this video to our WhatsApp number and return within 7 days, otherwise Elevate Mart will not accept it.', delay: 350 },
                  { type: 'allowed', text: 'All tags, labels, and original packaging must remain intact with the product.', delay: 400 },
                  { type: 'allowed', text: 'You must provide proof of purchase receipt or order confirmation.', delay: 450 },
                  { type: 'not-allowed', text: 'Personal care products, innerwear, and certain specific items are non-returnable for hygiene reasons.', delay: 500 },
                  { type: 'not-allowed', text: "Returns will not be accepted if you don't like the product after receiving it. Please review photos and videos carefully before purchasing.", delay: 550 },
                ].map((item, index) => (
                  <AnimatedOnScroll key={index} animationClass="animate-fade-in-right" delay={item.delay}>
                    <div className="flex items-start gap-3 py-3 border-b border-gray-50">
                      <span className={`mt-0.5 text-sm flex-shrink-0 ${item.type === 'allowed' ? 'text-gray-900' : 'text-red-400'}`}>
                        {item.type === 'allowed' ? '✓' : '✕'}
                      </span>
                      <p className={`text-sm leading-relaxed ${item.type === 'allowed' ? 'text-gray-700' : 'text-gray-500'}`}>
                        {item.text}
                      </p>
                    </div>
                  </AnimatedOnScroll>
                ))
              )}
            </div>
          </div>
        </AnimatedOnScroll>

        {/* Return Process */}
        <AnimatedOnScroll animationClass="animate-fade-in-left" delay={600}>
          <div className="mb-12 pb-12 border-b border-gray-100">
            <h2 className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-4">Return Process</h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              {policyData?.returnProcess || 'To apply for a return, please contact our customer service team. Mention your order number and the reason for return. We will review your request and inform you about the next steps.'}
            </p>
            <p className="text-sm text-gray-500 leading-relaxed">
              <span className="font-medium text-gray-700">Note: </span>
              Return shipping costs are generally borne by the customer, unless the product is defective or incorrect.
            </p>
          </div>
        </AnimatedOnScroll>

        {/* Processing Time */}
        <AnimatedOnScroll animationClass="animate-fade-in-left" delay={800}>
          <div className="mb-12 pb-12 border-b border-gray-100">
            <h2 className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-4">Processing Time</h2>
            <p
              className="text-base text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: policyData?.processingTime || 'Once we receive and inspect the returned product, your refund process may take <strong>5–7 business days</strong> to complete. The refund method will depend on your original payment method.'
              }}
            />
          </div>
        </AnimatedOnScroll>

        {/* Damaged Products */}
        <AnimatedOnScroll animationClass="animate-fade-in-left" delay={1000}>
          <div className="mb-12 pb-12 border-b border-gray-100">
            <h2 className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-4">Damaged or Defective Products</h2>
            <p
              className="text-base text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: policyData?.damagedProducts || 'If you receive a damaged or defective product, please inform us within <strong>48 hours</strong> of delivery. In such cases, we will arrange a replacement product or a full refund for you.'
              }}
            />
          </div>
        </AnimatedOnScroll>

        {/* Footer Note */}
        <AnimatedOnScroll animationClass="animate-fade-in-up" delay={1200}>
          <p className="text-sm text-gray-400 text-center leading-relaxed">
            {policyData?.footerNote || 'If you have any questions about our return policy, please feel free to contact us.'}
          </p>
        </AnimatedOnScroll>

      </div>
    </div>
  );
};

export default RefundPolicyPage;